import React, { useMemo, useState } from "react";
import type { SourceSelectionItem } from "../state";

interface Props {
    kind: "tracker" | "git";
    targets: SourceSelectionItem[];
    selection: SourceSelectionItem[];
    onChange: (selection: SourceSelectionItem[]) => void;
}

function Row({ item, selected, multi, onClick }: {
    item: SourceSelectionItem; selected: boolean; multi: boolean; onClick: () => void;
}) {
    return (
        <button type="button" onClick={onClick}
            style={{
                display: "flex", alignItems: "center", gap: 10, padding: "10px 13px",
                borderRadius: 11, background: "var(--bg-input)", width: "100%",
                border: selected ? "1px solid #103699" : "1px solid transparent",
                cursor: "pointer", fontFamily: "inherit", textAlign: "left", marginBottom: 8
            }}>
            {multi && (
                <span aria-hidden style={{
                    width: 16, height: 16, borderRadius: 5, flex: "0 0 auto",
                    border: selected ? "none" : "1.5px solid var(--divider)",
                    background: selected ? "#103699" : "transparent"
                }} />
            )}
            <span style={{
                width: 24, height: 24, borderRadius: 7, background: "#103699", color: "#fff",
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                fontSize: 10, fontWeight: 700, flex: "0 0 auto"
            }}>
                {(item.key ?? item.name).slice(0, 2).toUpperCase()}
            </span>
            <span style={{ flex: 1, fontSize: 12.5, fontWeight: 600 }}>
                {item.name}{" "}
                {item.key && (
                    <span style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, color: "var(--fg4)", fontWeight: 500 }}>
                        {item.key}
                    </span>
                )}
            </span>
        </button>
    );
}

export function TargetPicker({ kind, targets, selection, onChange }: Props) {
    const [query, setQuery] = useState("");
    const multi = kind === "git";
    const visible = useMemo(() => {
        const q = query.trim().toLowerCase();
        return targets.filter(t => !q || t.name.toLowerCase().includes(q));
    }, [targets, query]);
    const isSelected = (item: SourceSelectionItem) => selection.some(s => s.id === item.id);

    const toggle = (item: SourceSelectionItem) => {
        if (!multi) {
            onChange([item]);
            return;
        }
        onChange(isSelected(item) ? selection.filter(s => s.id !== item.id) : [...selection, item]);
    };

    const allVisibleSelected = visible.length > 0 && visible.every(isSelected);
    const selectAll = () => {
        if (allVisibleSelected) {
            const visibleIds = new Set(visible.map(v => v.id));
            onChange(selection.filter(s => !visibleIds.has(s.id)));
        } else {
            const merged = [...selection];
            for (const v of visible) if (!isSelected(v)) merged.push(v);
            onChange(merged);
        }
    };

    return (
        <div>
            {multi && (
                <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                    <input value={query} onChange={e => setQuery(e.target.value)}
                        placeholder="Search repositories"
                        style={{
                            flex: 1, padding: "8px 11px", borderRadius: 9,
                            border: "1px solid var(--divider)", fontSize: 12.5, fontFamily: "inherit"
                        }} />
                    <button type="button" onClick={selectAll}
                        style={{
                            padding: "0 12px", borderRadius: 9, border: "1px solid var(--divider)",
                            background: "var(--bg-paper)", fontSize: 12, fontWeight: 600,
                            cursor: "pointer", fontFamily: "inherit"
                        }}>
                        {allVisibleSelected ? "Clear all" : "Select all"}
                    </button>
                </div>
            )}
            <div style={{ maxHeight: 220, overflowY: "auto" }}>
                {visible.map(item => (
                    <Row key={item.id} item={item} selected={isSelected(item)} multi={multi}
                        onClick={() => toggle(item)} />
                ))}
                {visible.length === 0 && (
                    <div style={{ fontSize: 12, color: "var(--fg4)", padding: "8px 2px" }}>No matches</div>
                )}
            </div>
        </div>
    );
}
